import {
  completeFilter,
  type Filter,
  type FilterCondition,
  type SavedViewEntity,
  type Sort,
  type TableState,
} from '@crm/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PipelineView, StageCountView } from '../../lib/client.ts';
import { useMembers } from '../../lib/record-data.ts';
import { useSession } from '../../lib/session.tsx';
import { useSettings } from '../../lib/settings.ts';
import { RecordTable, useDebounced, type ColumnDef } from '../record-table/index.ts';
import type { FilterField } from '../record-table/filter-bar.tsx';
import { FilterControls } from '../record-table/filter-controls.tsx';
import { ViewsMenu } from '../record-table/views-menu.tsx';
import { Input } from '../ui/input.tsx';
import type { CardFieldSet } from '../../lib/card-fields.ts';
import { CardFieldList, CardFieldsMenu } from './card-fields.tsx';
import { KanbanBoard, type BoardSource } from './kanban-board.tsx';
import { DensityToggle, LayoutToggle } from './layout-toggle.tsx';
import { MultiBoard } from './multi-board.tsx';

/** What Multi board stacks: a list whose records sit in several pipelines (Projects). */
export interface ListLanes<T> {
  pipelines: readonly PipelineView[];
  /** The list's search, counts and move scoped to one pipeline. */
  apiFor: (pipeline: PipelineView) => ListApi<T>;
  noun: { one: string; many: string };
}

type Page<T> = { items: T[]; nextCursor: string | null };

/** How a list reads and moves its records: the same two calls back both layouts. */
export interface ListApi<T> {
  search: (body: {
    q?: string;
    filter?: Filter;
    sort?: Sort[];
    cursor?: string;
    limit: number;
  }) => Promise<Page<T>>;
  counts: (body: { q?: string; filter?: Filter }) => Promise<StageCountView[]>;
  move: (item: T, stageId: string) => Promise<unknown>;
  idOf: (item: T) => string;
  stageOf: (item: T) => string;
  titleOf: (item: T) => string;
  moved: BoardSource<T>['moved'];
  /** Sort keys the API accepts (column ids); others are not offered. */
  sortable: string[];
}

/**
 * One list, two layouts (RELAY-1038): the toolbar (saved views, table/board, card fields),
 * the filters beside the search, and either the server-paged table or the board — both reading
 * the same `TableState`, so switching layout keeps the filter, the search and the sort, and a saved
 * view restores whichever layout and card fields it was saved with. Given `lanes`, a third
 * layout, Multi board, stacks a board per pipeline (`MultiBoard`).
 */
export function RecordList<T>({
  entity,
  state,
  setState,
  activeViewId,
  filter: viewFilter,
  conditions,
  onConditionsChange,
  filterFields,
  quickFilters,
  api,
  columns,
  pipeline,
  lanes,
  cardSet,
  canMove,
  hideTerminal,
  onOpen,
  onMoved,
  emptyMessage,
  searchPlaceholder,
  selection,
  onSelectionChange,
}: {
  entity: SavedViewEntity;
  state: TableState;
  setState: (next: TableState, viewId?: string | null) => void;
  activeViewId: string | null;
  /** The whole filter the API is asked with (quick filters and conditions together). */
  filter: Filter | undefined;
  conditions: FilterCondition[];
  onConditionsChange: (next: FilterCondition[]) => void;
  filterFields: readonly FilterField[];
  quickFilters?: ReactNode;
  api: ListApi<T>;
  columns: ColumnDef<T, unknown>[];
  pipeline: PipelineView;
  /** Offers Multi board: a board per pipeline, stacked. */
  lanes?: ListLanes<T> | undefined;
  cardSet: CardFieldSet<T>;
  canMove: boolean;
  hideTerminal?: boolean;
  onOpen: (item: T) => void;
  onMoved?: () => void | Promise<void>;
  emptyMessage: string;
  searchPlaceholder: string;
  /** Row selection for bulk actions (table layout only): both, or neither. */
  selection?: ReadonlySet<string> | undefined;
  onSelectionChange?: ((next: Set<string>) => void) | undefined;
}) {
  const qc = useQueryClient();
  const session = useSession();
  const [settings] = useSettings(session.me.user.id);
  const { nameOf } = useMembers();
  // Multi board only where the list has lanes; a stray one (a hand-edited link) is the board.
  const layout = state.layout === 'multiboard' && !lanes ? 'board' : (state.layout ?? 'table');
  const boardLike = layout !== 'table';
  const density = state.density ?? 'normal';
  const chosen = state.card?.fields ?? cardSet.defaults;
  const q = state.q?.trim() || undefined;
  // An unfilled filter row stays on screen but is never sent: the API answers 400 to it.
  const filter = useMemo(() => completeFilter(viewFilter), [viewFilter]);
  const scope = useMemo(
    () => ({ ...(q ? { q } : {}), ...(filter ? { filter } : {}) }),
    [q, filter],
  );

  const tableSource = useMemo(
    () => ({
      // A different filter or pipeline is a different data set: the table starts again from
      // page one (Projects pick their pipeline, TS-1749).
      key: `${entity}:${pipeline.id}:${JSON.stringify(filter ?? null)}`,
      sortable: api.sortable,
      fetchPage: ({
        cursor,
        limit,
        sort,
        filter: text,
      }: {
        cursor: string | null;
        limit: number;
        sort: { id: string; desc: boolean } | null;
        filter: string;
      }) =>
        api.search({
          limit: Math.min(limit, 200),
          ...(text.trim() ? { q: text.trim() } : {}),
          ...(filter ? { filter } : {}),
          ...(sort ? { sort: [{ field: sort.id, dir: sort.desc ? 'desc' : 'asc' }] } : {}),
          ...(cursor ? { cursor } : {}),
        }),
    }),
    [entity, pipeline.id, filter, api],
  );

  const boardSource = useMemo<BoardSource<T>>(
    () => ({
      scope: [scope, state.sort ?? null, pipeline.id],
      counts: () => api.counts(scope),
      page: (stageId, cursor, limit) =>
        api.search({
          ...scope,
          // A column is the list's filter narrowed to its stage.
          filter: filter
            ? { and: [filter, { field: 'stageId', op: 'eq', value: stageId }] }
            : { field: 'stageId', op: 'eq', value: stageId },
          ...(state.sort ? { sort: [state.sort] } : {}),
          limit,
          ...(cursor ? { cursor } : {}),
        }),
      move: api.move,
      idOf: api.idOf,
      stageOf: api.stageOf,
      titleOf: api.titleOf,
      moved: api.moved,
    }),
    [scope, filter, state.sort, api, pipeline.id],
  );

  const filterControls = (
    <FilterControls fields={filterFields} conditions={conditions} onChange={onConditionsChange} />
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {quickFilters}
        {boardLike && (
          <BoardSearch
            value={state.q ?? ''}
            placeholder={searchPlaceholder}
            onChange={(text) => setState({ ...state, q: text || undefined })}
          />
        )}
        {layout !== 'table' && filterControls}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {boardLike && (
            <CardFieldsMenu
              fields={cardSet.fields}
              chosen={chosen}
              onChange={(fields) => setState({ ...state, card: { fields } })}
            />
          )}
          <ViewsMenu
            entity={entity}
            state={state}
            activeId={activeViewId}
            onApply={(next, id) => setState(next, id)}
          />
          {boardLike && (
            <DensityToggle
              density={density}
              onChange={(next) =>
                setState({ ...state, density: next === 'normal' ? undefined : next })
              }
            />
          )}
          <LayoutToggle
            layout={layout}
            multi={!!lanes}
            onChange={(next) => setState({ ...state, layout: next === 'table' ? undefined : next })}
          />
        </div>
      </div>
      {layout === 'multiboard' && lanes ? (
        <MultiBoard
          name={entity}
          noun={lanes.noun}
          pipelines={lanes.pipelines}
          apiFor={lanes.apiFor}
          q={q}
          filter={filter}
          sort={state.sort}
          hideTerminal={hideTerminal ?? false}
          density={density}
          canMove={canMove}
          onOpen={onOpen}
          onMoved={async () => {
            await qc.invalidateQueries({ queryKey: ['records'] });
            // A move changes that project in every lane it sits in: re-read the others too.
            await qc.invalidateQueries({ queryKey: ['board', entity] });
            await onMoved?.();
          }}
          renderCard={(item) => (
            <CardFieldList item={item} set={cardSet} chosen={chosen} ctx={{ nameOf }} />
          )}
        />
      ) : layout === 'table' ? (
        <RecordTable
          columns={columns}
          source={tableSource}
          getRowId={api.idOf}
          mode={settings.pagination}
          pageSize={settings.pageSize}
          hideColumnsMenu
          filterPlaceholder={searchPlaceholder}
          toolbar={filterControls}
          emptyMessage={emptyMessage}
          tableState={state}
          onTableStateChange={(next) => setState(next)}
          onRowClick={onOpen}
          {...(selection && onSelectionChange ? { selection, onSelectionChange } : {})}
        />
      ) : (
        <KanbanBoard
          name={entity}
          pipeline={pipeline}
          source={boardSource}
          hideTerminal={hideTerminal ?? false}
          density={density}
          canMove={canMove}
          onOpen={onOpen}
          onMoved={async () => {
            // The table and any record page showing these records re-read on their next look.
            await qc.invalidateQueries({ queryKey: ['records'] });
            await onMoved?.();
          }}
          renderCard={(item) => (
            <CardFieldList item={item} set={cardSet} chosen={chosen} ctx={{ nameOf }} />
          )}
        />
      )}
    </div>
  );
}

/** The board's own search box, debounced into the view state's `q` (the table has its own). */
function BoardSearch({
  value,
  placeholder,
  onChange,
}: {
  value: string;
  placeholder: string;
  onChange: (text: string) => void;
}) {
  const [text, setText] = useState(value);
  const settled = useDebounced(text.trim(), 250);
  useEffect(() => {
    if (settled !== value) onChange(settled);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when the typed text settles
  }, [settled]);
  return (
    <Input
      aria-label="Search"
      placeholder={placeholder}
      value={text}
      onChange={(e) => setText(e.target.value)}
      className="h-8 w-56"
    />
  );
}
